import { useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { backendApi } from '../lib/api';
import { Icon } from './Icon';
import { useModalEscape } from '../lib/useModalEscape';
import {
  RESTORE_WINDOW_DAYS, MISSING_ENDPOINT_MESSAGE, auditSummary, changedFields, fmtDateTime,
  isMissingEndpoint, isRestoreWindowEnded, parseAudit, restoreDeadline, restoreWindowEnded, ticketRef,
  type AuditRow,
} from '../lib/supportManage';

const enc = encodeURIComponent;
const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

/** A small icon button with an accessible name. When `disabledReason` is set it
 *  is disabled and the reason is the tooltip (on a wrapper too, because some
 *  browsers show no tooltip for a disabled button). */
export function IconAction({ label, icon, onClick, disabledReason, danger, testId }: {
  label: string; icon: string; onClick: () => void; disabledReason?: string; danger?: boolean; testId?: string;
}) {
  return (
    <span title={disabledReason || label} className="icon-action-wrap">
      <button type="button" className={'btn-icon' + (danger ? ' danger' : '')} aria-label={label}
        title={disabledReason || label} disabled={!!disabledReason} data-testid={testId}
        onClick={(e) => { e.stopPropagation(); onClick(); }}>
        <Icon name={icon} size={15} />
      </button>
    </span>
  );
}

/** A modal question with a named subject. Sits above the ticket modal; Escape
 *  and a click on the scrim cancel only this dialog. */
export function ConfirmDialog({ title, children, confirmLabel, busy, error, onConfirm, onCancel }: {
  title: string; children: ReactNode; confirmLabel: string; busy?: boolean; error?: string;
  onConfirm: () => void; onCancel: () => void;
}) {
  useModalEscape(onCancel);
  const tid = useId(); const did = useId();
  return createPortal(
    <div className="modal-scrim" style={{ zIndex: 600 }} onClick={(e) => { e.stopPropagation(); if (!busy) onCancel(); }}>
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby={tid} aria-describedby={did}
        style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h2 className="card-title" id={tid} style={{ marginTop: 0 }}>{title}</h2>
        <div id={did} style={{ marginBottom: 14 }}>{children}</div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-danger btn-sm" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>, document.body);
}

export interface ManagedTicket {
  id: string; title: string; status: string; priority: string | null; assigned_to?: string | null;
  deleted_at?: string | null; restorable_until?: string | null;
}

/** Confirm, naming the ticket, then DELETE. `onDeleted` runs after success. */
export function DeleteTicketDialog({ ticket, onDeleted, onCancel, onMissing }: {
  ticket: ManagedTicket; onDeleted: () => void; onCancel: () => void; onMissing: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function go() {
    setBusy(true); setError('');
    try {
      await backendApi.delete(`/tickets/admin/${enc(ticket.id)}`);
      onDeleted();
    } catch (err: any) {
      if (isMissingEndpoint(err)) { setError(MISSING_ENDPOINT_MESSAGE); onMissing(); } else setError(err.message);
      setBusy(false);
    }
  }
  return (
    <ConfirmDialog title="Delete this ticket?" confirmLabel="Delete ticket" busy={busy} error={error}
      onConfirm={go} onCancel={onCancel}>
      <p style={{ margin: '0 0 8px' }}><strong>{ticketRef(ticket)}</strong></p>
      <p className="muted" style={{ margin: 0 }}>
        The ticket is hidden from the customer too, not only from this list. It can be
        restored for {RESTORE_WINDOW_DAYS} days; after that it can no longer be brought back.
      </p>
    </ConfirmDialog>
  );
}

/** Title, status, priority and assignee. Sends only what changed. */
export function EditTicketModal({ ticket, onSaved, onClose, onMissing }: {
  ticket: ManagedTicket; onSaved: () => void; onClose: () => void; onMissing: () => void;
}) {
  useModalEscape(onClose);
  const before = {
    title: ticket.title || '', status: ticket.status, priority: ticket.priority || '',
    assigned_to: ticket.assigned_to || '',
  };
  const [form, setForm] = useState(before);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tid = useId();
  const diff = changedFields(before, { ...form, title: form.title.trim(), assigned_to: form.assigned_to.trim() });
  const titleBlank = !form.title.trim();
  const set = (k: keyof typeof before) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (busy || titleBlank || Object.keys(diff).length === 0) return;
    // An emptied assignee clears it (null), rather than sending an empty string.
    const body: Record<string, unknown> = { ...diff };
    if ('assigned_to' in body && body.assigned_to === '') body.assigned_to = null;
    setBusy(true); setError('');
    try {
      await backendApi.patch(`/tickets/admin/${enc(ticket.id)}`, body);
      onSaved();
    } catch (err: any) {
      if (isMissingEndpoint(err)) { setError(MISSING_ENDPOINT_MESSAGE); onMissing(); } else setError(err.message);
      setBusy(false);
    }
  }

  return createPortal(
    <div className="modal-scrim" style={{ zIndex: 600 }} onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby={tid} style={{ maxWidth: 480 }}
        onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="modal-head">
          <h2 className="card-title" id={tid} style={{ margin: 0 }}>Edit ticket {ticketRef(ticket)}</h2>
          <button type="button" className="btn-icon conv-modal-close" aria-label="Close edit form" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <label className="field"><span>Title</span>
          <input value={form.title} maxLength={300} onChange={(e) => set('title')(e.target.value)} /></label>
        <label className="field"><span>Status</span>
          <select value={form.status} onChange={(e) => set('status')(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></label>
        <label className="field"><span>Priority</span>
          <select value={form.priority} onChange={(e) => set('priority')(e.target.value)}>
            {!before.priority && <option value="">not set</option>}
            {PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></label>
        <label className="field"><span>Assignee</span>
          <input value={form.assigned_to} placeholder="Nobody assigned" onChange={(e) => set('assigned_to')(e.target.value)} /></label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm"
            disabled={busy || titleBlank || Object.keys(diff).length === 0}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </div>, document.body);
}

/** Restore a deleted ticket; says so when the 30 days are over instead. */
export function RestoreControl({ ticket, canWrite, onRestored, onMissing, compact }: {
  ticket: ManagedTicket; canWrite: boolean; onRestored: () => void; onMissing: () => void; compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ended, setEnded] = useState(false);
  const isEnded = ended || restoreWindowEnded(ticket);
  const until = restoreDeadline(ticket);

  async function go() {
    setBusy(true); setError('');
    try {
      await backendApi.post(`/tickets/admin/${enc(ticket.id)}/restore`);
      onRestored();
    } catch (err: any) {
      if (isRestoreWindowEnded(err)) setEnded(true);
      else if (isMissingEndpoint(err)) { setError(MISSING_ENDPOINT_MESSAGE); onMissing(); }
      else setError(err.message);
      setBusy(false);
    }
  }

  if (isEnded) return <span className="muted small" data-testid="restore-ended">Restore window ended</span>;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      {canWrite && (
        <button type="button" className={'btn btn-sm' + (compact ? '' : ' btn-primary')} onClick={go} disabled={busy}
          aria-label={`Restore ticket ${ticketRef(ticket)}`}
          title={until ? `Can be restored until ${fmtDateTime(until.toISOString())}` : undefined}>
          {busy ? 'Restoring…' : 'Restore'}
        </button>
      )}
      {error && <span className="small" role="alert" style={{ color: '#EF4444', marginLeft: 8 }}>{error}</span>}
    </span>
  );
}

const clip = (s: string | null, n = 240) => (s == null ? '(empty)' : s.length > n ? s.slice(0, n) + '…' : s);

/** Who changed what, collapsed until opened. Hides itself if the backend has no
 *  audit route (a missing-route 404 — see isMissingEndpoint; a real "Ticket not found" is shown as an error). Reloads when `refreshKey` changes after first open. */
export function AuditTrail({ ticketId, refreshKey }: { ticketId: string; refreshKey: number }) {
  const [opened, setOpened] = useState(false);
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState('');
  const [gone, setGone] = useState(false);
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    backendApi.get<unknown>(`/tickets/admin/${enc(ticketId)}/audit`)
      .then((d) => { if (!cancelled) { setRows(parseAudit(d)); setError(''); } })
      .catch((err: any) => {
        if (cancelled) return;
        if (isMissingEndpoint(err)) setGone(true); else setError(err.message);
      });
    return () => { cancelled = true; };
  }, [opened, ticketId, refreshKey]);

  if (gone) return null;
  return (
    <details style={{ marginTop: 16 }} data-testid="audit-trail" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) setOpened(true); }}>
      <summary style={{ cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Audit trail</summary>
      {error && <div className="alert alert-error" style={{ marginTop: 8 }}>{error}</div>}
      {!error && !rows && <p className="muted small">Loading…</p>}
      {rows && rows.length === 0 && <p className="muted small">No changes recorded.</p>}
      {rows && rows.length > 0 && (
        <ul className="plain-list" style={{ marginTop: 8 }}>
          {rows.map((r) => (
            <li key={r.id} data-testid="audit-row">
              <div><strong>{r.actor_name || r.actor_id || 'Someone'}</strong>
                <span className="muted small"> · {fmtDateTime(r.created_at)}</span></div>
              <div className="small" title={auditSummary(r)}>
                {r.response_id && <span className="muted">message · </span>}
                {r.field
                  ? <>{r.action.replace(/_/g, ' ')} — {r.field}: <span className="muted">{clip(r.previous_value)}</span> → {clip(r.new_value)}</>
                  : r.action.replace(/_/g, ' ')}
              </div>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

export interface ManagedReply {
  id: string; content: string | null; author_type: string; author_name?: string; created_at: string;
  edited_at?: string | null; deleted_at?: string | null; deleted_by_name?: string | null;
}

/** One conversation entry: normal, edited, being edited, or removed. */
export function ReplyItem({ ticketId, reply, canManage, onChanged, onMissing }: {
  ticketId: string; reply: ManagedReply; canManage: boolean; onChanged: () => void; onMissing: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(reply.content ?? '');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const path = `/tickets/admin/${enc(ticketId)}/responses/${enc(reply.id)}`;

  if (reply.deleted_at) {
    return (
      <li className={'reply-removed' + (reply.author_type === 'admin' ? ' reply-admin' : '')} data-testid="reply-removed">
        <span className="muted">
          Message removed by {reply.deleted_by_name || 'an admin'} · {fmtDateTime(reply.deleted_at)}
        </span>
        {reply.content != null && (
          <details style={{ marginTop: 6 }}>
            <summary className="small" style={{ cursor: 'pointer' }}>Original text (audit view)</summary>
            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }} className="muted">{reply.content}</div>
          </details>
        )}
      </li>
    );
  }

  async function save() {
    const next = text.trim();
    if (!next || busy) return;
    setBusy(true); setError('');
    try {
      await backendApi.patch(path, { content: next });
      setEditing(false); setBusy(false); onChanged();
    } catch (err: any) {
      if (isMissingEndpoint(err)) { setError(MISSING_ENDPOINT_MESSAGE); onMissing(); } else setError(err.message);
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true); setError('');
    try {
      await backendApi.delete(path);
      setConfirming(false); setBusy(false); onChanged();
    } catch (err: any) {
      if (isMissingEndpoint(err)) { setError(MISSING_ENDPOINT_MESSAGE); onMissing(); } else setError(err.message);
      setBusy(false);
    }
  }

  return (
    <li className={reply.author_type === 'admin' ? 'reply-admin' : ''}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ flex: 1 }}>
          <strong>{reply.author_name || reply.author_type}</strong>
          <span className="muted small"> · {new Date(reply.created_at).toLocaleString()}</span>
          {reply.edited_at && <span className="muted small" data-testid="edited-marker"> · edited {fmtDateTime(reply.edited_at)}</span>}
        </span>
        {canManage && !editing && (
          <>
            <IconAction label="Edit message" icon="pencil" onClick={() => { setText(reply.content ?? ''); setError(''); setEditing(true); }} />
            <IconAction label="Delete message" icon="trash" danger onClick={() => { setError(''); setConfirming(true); }} />
          </>
        )}
      </div>
      {editing ? (
        <div style={{ marginTop: 6 }}>
          <textarea className="textarea" aria-label="Edit message text" value={text} onChange={(e) => setText(e.target.value)} />
          {error && <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={save}
              disabled={busy || !text.trim() || text.trim() === (reply.content ?? '').trim()}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => { setEditing(false); setError(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{reply.content}</div>
      )}
      {!editing && !confirming && error && <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
      {confirming && (
        <ConfirmDialog title="Delete this message?" confirmLabel="Delete message" busy={busy} error={error}
          onConfirm={remove} onCancel={() => { setConfirming(false); setError(''); }}>
          <p className="muted" style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>
            “{(reply.content ?? '').slice(0, 200)}{(reply.content ?? '').length > 200 ? '…' : ''}”
          </p>
          <p style={{ margin: 0 }}>The customer will see “This message was removed by support” in its place. Their own messages are not affected.</p>
        </ConfirmDialog>
      )}
    </li>
  );
}
